import React, { useMemo } from "react";
import EnvironmentalContextSection from "./EnvironmentalContextSection";
import { REASON_KEYS } from "./generalConditionsReasons";
import "../../styles/components/GeneralConditionsContext.css";
import {
  Activity,
  CalendarDays,
  CircleGauge,
  Clock3,
  MapPin,
  ShieldAlert,
  Wind,
} from "lucide-react";

const STATE_KEYS = Object.freeze({
  biological: Object.freeze({
    strong_environmental_limitation:
      "fishing_today_general_biological_strong_environmental_limitation",
    potential_environmental_limitation:
      "fishing_today_general_biological_potential_environmental_limitation",
    no_major_limitation_detected:
      "fishing_today_general_biological_no_major_limitation_detected",
    insufficient_biological_data:
      "fishing_today_general_biological_insufficient_biological_data",
  }),
  fishability: Object.freeze({
    favorable: "fishing_today_general_fishability_favorable",
    manageable: "fishing_today_general_fishability_manageable",
    difficult: "fishing_today_general_fishability_difficult",
    very_difficult: "fishing_today_general_fishability_very_difficult",
    insufficient_data: "fishing_today_general_fishability_insufficient_data",
  }),
  safety: Object.freeze({
    normal_caution: "fishing_today_general_safety_normal_caution",
    elevated_caution: "fishing_today_general_safety_elevated_caution",
    dangerous: "fishing_today_general_safety_dangerous",
    insufficient_data: "fishing_today_general_safety_insufficient_data",
  }),
  confidence: Object.freeze({
    low: "fishing_today_general_confidence_low",
    moderate: "fishing_today_general_confidence_moderate",
    high: "fishing_today_general_confidence_high",
  }),
});

const WATERBODY_KEYS = Object.freeze({
  marine_coastal: "fishing_today_general_waterbody_marine_coastal",
  marine_offshore: "fishing_today_general_waterbody_marine_offshore",
  freshwater_stillwater: "fishing_today_general_waterbody_freshwater_stillwater",
  freshwater_river_stream:
    "fishing_today_general_waterbody_freshwater_river_stream",
  tidal_freshwater: "fishing_today_general_waterbody_tidal_freshwater",
  unknown_waterbody: "fishing_today_general_waterbody_unknown",
});

const stateLabel = (t, dimension, state) => {
  const key = STATE_KEYS[dimension]?.[state];
  return typeof key === "string" ? t(key) : t("fishing_today_general_state_unknown");
};

const reasonLabels = (t, reasonCodes = []) => {
  const seen = new Set();
  return (Array.isArray(reasonCodes) ? reasonCodes : [])
    .map((code) => typeof REASON_KEYS[code] === "string" ? REASON_KEYS[code] : "fishing_today_general_reason_unknown")
    .filter((key) => {
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 3)
    .map((key) => t(key));
};

const displayState = (dimension, state, availability, coverageKnown = true) => {
  // Old responses cannot establish G7's complete/fresh reassurance gate.
  if ((dimension === "fishability" && ["favorable", "manageable"].includes(state)) ||
      (dimension === "safety" && state === "normal_caution")) {
    if (availability !== "available") return "insufficient_data";
  }
  // Reserved wire enums do not activate unapproved General biology in the UI.
  if (dimension === "biological" && typeof STATE_KEYS.biological[state] === "string")
    return "insufficient_biological_data";
  if (dimension === "confidence" && ["moderate", "high"].includes(state))
    return coverageKnown ? "moderate" : "low";
  return state;
};

const formatLocalTime = (value, formatter, t) => {
  const match = String(value || "").match(/T(\d{2}):(\d{2})/);
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return t("fishing_today_general_state_unknown");
  const date = new Date(Date.UTC(2020, 0, 1, Number(match[1]), Number(match[2])));
  return formatter.format(date);
};

const formatLocalDate = (value, formatter, t) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return t("fishing_today_general_state_unknown");
  const date = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value)
    return t("fishing_today_general_state_unknown");
  return formatter.format(date);
};

const AssessmentCard = ({ assessment, dimension, icon: Icon, t, coverageKnown = true }) => {
  const rawState = assessment?.state || "unknown";
  const state = displayState(dimension, rawState, assessment?.availability, coverageKnown);
  const codes = state === "insufficient_data" && rawState !== state ?
    [dimension === "safety" ? "general_safety_partial_weather" : "general_fishability_insufficient_data"] : assessment?.reasonCodes;
  const reasons = reasonLabels(t, Array.isArray(codes) ? codes.filter(code =>
    !(dimension === "safety" && code === "general_safety_limited_data")) : []);
  const stateClass = typeof STATE_KEYS[dimension]?.[state] === "string" ? state : "unknown";
  return (
    <article
      className={`rp-general-conditions__assessment rp-general-conditions__assessment--${dimension} rp-general-conditions__assessment--${stateClass}`}
    >
      <div className="rp-general-conditions__assessment-heading">
        <Icon size={19} aria-hidden="true" />
        <h5>{t(`fishing_today_general_${dimension}_title`)}</h5>
      </div>
      <strong>{stateLabel(t, dimension, state)}</strong>
      {dimension === "biological" ? <p>{t("fishing_today_general_biology_explanation")}</p> : null}
      {assessment?.availability === "partial" || assessment?.availability === "unavailable" ? (
        <small className="rp-general-conditions__coverage" role="status">{t(`fishing_today_general_${assessment.availability === "partial" ? "partial" : "unavailable"}`)}</small>
      ) : null}
      {dimension === "confidence" ? (
        <p>{t("fishing_today_general_confidence_explanation")}</p>
      ) : null}
      {reasons.length ? (
        <ul>
          {reasons.map((reason) => <li key={reason}>{reason}</li>)}
        </ul>
      ) : null}
      {dimension === "safety" ? (
        <small>{t("fishing_today_general_safety_coverage_limited")}</small>
      ) : null}
    </article>
  );
};

const StatePair = ({ dimension, state, availability, t, coverageKnown = true }) => (
  <span>
    <small>{t(`fishing_today_general_${dimension}_title`)}</small>
    <strong>{stateLabel(t, dimension, displayState(dimension, state, availability, coverageKnown))}</strong>
    {availability && availability !== "available" ? <small>{t(`fishing_today_general_${availability === "partial" ? "partial" : "unavailable"}`)}</small> : null}
  </span>
);

const GeneralTimeline = ({ formatter, points, t }) => {
  if (!Array.isArray(points) || !points.length) return null;
  return (
    <section className="rp-general-conditions__forecast" aria-labelledby="general-hourly-title">
      <div className="rp-general-conditions__forecast-heading">
        <Clock3 size={18} aria-hidden="true" />
        <h5 id="general-hourly-title">{t("fishing_today_general_hourly_title")}</h5>
      </div>
      <ol
        className="rp-general-conditions__hourly"
        aria-label={t("fishing_today_general_hourly_aria")}
        tabIndex="0"
      >
        {points.filter(point => point && typeof point === "object").map((point, index) => (
          <li key={`${point.timestampUtc || point.timestampLocal || "hour"}-${index}`}>
            <time dateTime={point.timestampLocal || point.timestampUtc}>
              {formatLocalTime(point.timestampLocal || point.timestampUtc, formatter, t)}
              {!point.timestampLocal && point.timestampUtc ? " UTC" : ""}
            </time>
            <StatePair dimension="fishability" state={point.fishability?.state} availability={point.fishability?.availability} t={t} />
            <StatePair dimension="safety" state={point.safety?.state} availability={point.safety?.availability} t={t} />
          </li>
        ))}
      </ol>
    </section>
  );
};

const GeneralDaily = ({ days, formatter, t }) => {
  if (!Array.isArray(days) || !days.length) return null;
  return (
    <section className="rp-general-conditions__forecast" aria-labelledby="general-daily-title">
      <div className="rp-general-conditions__forecast-heading">
        <CalendarDays size={18} aria-hidden="true" />
        <h5 id="general-daily-title">{t("fishing_today_general_daily_title")}</h5>
      </div>
      <ol className="rp-general-conditions__daily">
        {days.filter(day => day && typeof day === "object").map((day) => (
          <li key={day.localDate}>
            <time dateTime={day.localDate}>{formatLocalDate(day.localDate, formatter, t)}{day.dateBasis === "utc_fallback" ? " UTC" : ""}</time>
            <div>
              <StatePair dimension="fishability" state={day.fishabilityState} availability={day.availability?.fishability} t={t} />
              <StatePair dimension="safety" state={day.safetyState} availability={day.availability?.safety} t={t} />
              <StatePair dimension="biological" state={day.biologicalState} t={t} />
              <StatePair dimension="confidence" state={day.confidenceState} coverageKnown={day.availability?.fishability === "available" && day.availability?.safety === "available"} t={t} />
            </div>
            {Number.isFinite(day.expectedPointCount) ? (
              <p className="rp-general-conditions__coverage">
                {t("fishing_today_general_daily_coverage", { evaluated: day.coverage?.fishability?.evaluated ?? 0,
                  expected: day.expectedPointCount, missing: day.coverage?.fishability?.missing ?? day.expectedPointCount })}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
};

export default function GeneralConditionsSection({ data, locale = "en", t }) {
  // Profiling found 63 Intl formatter constructions per 60-hour render. Keep
  // just two per locale, not one per timeline/day slot or unchanged rerender.
  const formatters = useMemo(() => {
    let supportedLocale = locale;
    try { new Intl.Locale(locale); } catch { supportedLocale = "en"; }
    return {
      locale: supportedLocale,
      time: new Intl.DateTimeFormat(supportedLocale, { hour: "numeric", minute: "2-digit", timeZone: "UTC" }),
      date: new Intl.DateTimeFormat(supportedLocale, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }),
    };
  }, [locale]);
  if (!data?.current) {
    return (
      <section className="rp-general-conditions" aria-labelledby="general-conditions-title">
        <header>
          <h4 id="general-conditions-title">{t("fishing_today_general_title")}</h4>
          <p>{t("fishing_today_general_intro")}</p>
        </header>
        <p className="rp-species-intel__general" role="status">
          {t("fishing_today_general_fallback")}
        </p>
      </section>
    );
  }

  const rawContext = data.waterbodyContext || data.current.waterbodyContext;
  const context = typeof WATERBODY_KEYS[rawContext] === "string" ? rawContext : "unknown_waterbody";
  const contextKey = WATERBODY_KEYS[context] || WATERBODY_KEYS.unknown_waterbody;

  return (
    <section
      className="rp-general-conditions"
      aria-labelledby="general-conditions-title"
      aria-live="polite"
    >
      <header>
        <div>
          <h4 id="general-conditions-title">{t("fishing_today_general_title")}</h4>
          <p>{t("fishing_today_general_intro")}</p>
        </div>
        <span className="rp-general-conditions__waterbody">
          {t("fishing_today_general_waterbody_label")}: {t(contextKey)}
        </span>
      </header>

      {context === "unknown_waterbody" ? (
        <div className="rp-general-conditions__guidance" role="status">
          <MapPin size={19} aria-hidden="true" />
          <p>{t("fishing_today_general_location_guidance")}</p>
        </div>
      ) : null}

      <div className="rp-general-conditions__grid">
        <AssessmentCard assessment={data.current.biologicalConditions || data.current.biological} dimension="biological" icon={Activity} t={t} />
        <AssessmentCard assessment={data.current.fishability} dimension="fishability" icon={Wind} t={t} />
        <AssessmentCard assessment={data.current.safety} dimension="safety" icon={ShieldAlert} t={t} />
        <AssessmentCard assessment={data.current.confidence} dimension="confidence" coverageKnown={["fishability", "safety", "environmentalContext"].every(key => data.current.confidence?.dimensions?.[key] === "complete")} icon={CircleGauge} t={t} />
      </div>

      <EnvironmentalContextSection context={data.current.environmentalContext} legacy={data.current.biological?.contextObservations} locale={formatters.locale} t={t} />
      <GeneralTimeline formatter={formatters.time} points={data.timeline} t={t} />
      <GeneralDaily days={data.daily} formatter={formatters.date} t={t} />
    </section>
  );
}
